import {
  buildProductStepPath,
  getCompletedSteps,
  getItemTypeForProductType,
  getStepsForProduct,
  parsePreselectedTeeth,
  StepKeys,
  StepNavigation,
  useBulkProductGroup,
  useBulkTeethSelection,
  useOrderWorkflow,
  WorkflowStep,
  buildAddProductPath,
  useProductWorkflow,
  useStepSaveCoordinator,
} from "@jasper/product-workflows";
import { useOrderConfiguration } from "@jasper/use-cases";
import { Box, Divider, Group, Stack, Title } from "@mantine/core";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { notifications } from "@mantine/notifications";
import { useProductTranslations } from "@jasper/translations";
import {
  Navigate,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import ProductStepper from "./ProductStepper";
import { Text } from "@mantine/core";
import { useScrollSpy } from "@mantine/hooks";

type OrderConfiguration = ReturnType<typeof useOrderConfiguration>;

export type ConfigurableProduct = NonNullable<
  NonNullable<OrderConfiguration["order"]>["products"]
>[number];

export interface ProductConfigurationContext {
  content: ReactNode;
  product: ConfigurableProduct | undefined;
  orderConfiguration: OrderConfiguration;
}

interface ContentProps {
  orderId: string;
  currentStep: string | undefined;
  product: ConfigurableProduct;
  orderConfiguration: OrderConfiguration;
  setActiveStep: (step: StepKeys) => void;
  availableSteps: Readonly<WorkflowStep<StepKeys>[]>;
}

const ProductConfigurationContent = ({
  orderId,
  currentStep,
  product,
  orderConfiguration,
  setActiveStep,
  availableSteps,
}: ContentProps) => {
  const navigate = useNavigate();
  const currentStepComponentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const isProgrammaticallyScrolling = useRef(false);
  const [isNavigatingAfterSave, setIsNavigatingAfterSave] = useState(false);
  const stepSaveCoordinator = useStepSaveCoordinator();
  const { t: translateProduct } = useProductTranslations();

  const { search } = useLocation();
  const scrollSpy = useScrollSpy({
    selector: "[data-product-step]",
    offset: 200,
  });

  const preselectedTeeth = useMemo(
    () => parsePreselectedTeeth(search),
    [search]
  );

  const bulkGroup = useBulkProductGroup({
    leaderProduct: product,
    products: orderConfiguration.order?.products ?? [],
  });
  const { isBulkSupported, bulkProductFollowerIds } = bulkGroup;

  const { goNext, goBack } = useOrderWorkflow({
    orderId,
    bulkProductIds: bulkProductFollowerIds,
  });
  const { goTo, activeSteps } = useProductWorkflow({
    steps: getStepsForProduct(product),
    orderId,
    productId: product.id,
    completedStepKeys: getCompletedSteps(product),
    onComplete: () => {},
    currentStepKey: currentStep as StepKeys | undefined,
    product,
    bulkProductIds: bulkProductFollowerIds,
  });

  const isMutationInFlight =
    orderConfiguration.isUpdatingProduct ||
    orderConfiguration.isUpdatingProducts ||
    orderConfiguration.isAddingProduct ||
    orderConfiguration.isAddingProducts ||
    orderConfiguration.isRemovingProducts;
  const isMutationInFlightRef = useRef(isMutationInFlight);
  useEffect(() => {
    isMutationInFlightRef.current = isMutationInFlight;
  }, [isMutationInFlight]);

  const itemType = getItemTypeForProductType(product.type);

  const { applyTeethSelection } = useBulkTeethSelection({
    orderId,
    leaderProduct: product,
    bulkGroup,
    itemType,
    orderConfiguration,
  });

  useEffect(() => {
    if (scrollSpy.active === -1) return;
    const activeStep = availableSteps[scrollSpy.active]?.key;
    if (!activeStep) return;

    setActiveStep(activeStep);
  }, [scrollSpy.active]);

  useEffect(() => {
    if (isFirstRender.current || isProgrammaticallyScrolling.current) {
      return;
    }

    if (scrollSpy.active === -1) {
      return;
    }

    const activeStep = availableSteps[scrollSpy.active]?.key;
    if (!activeStep || activeStep === currentStep) {
      return;
    }

    navigate(
      buildProductStepPath(
        orderId,
        product.id,
        activeStep,
        bulkProductFollowerIds
      ),
      { replace: true }
    );
  }, [scrollSpy.active]);

  useEffect(() => {
    if (!currentStepComponentRef.current) {
      return;
    }

    const currentStepIndex = availableSteps.findIndex(
      step => step.key === currentStep
    );

    if (currentStepIndex === -1) {
      return;
    }

    if (scrollSpy.active === currentStepIndex) {
      return;
    }

    const isFirstStep = currentStepIndex === 0;
    if (isFirstStep && (scrollSpy.active === -1 || scrollSpy.active === 0)) {
      isFirstRender.current = false;
      return;
    }

    const scrollToStep = () => {
      if (isFirstStep) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      currentStepComponentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    };

    isProgrammaticallyScrolling.current = true;
    const handleScrollEnd = () => {
      isProgrammaticallyScrolling.current = false;
    };
    addEventListener("scrollend", handleScrollEnd, { once: true });
    const frame = window.requestAnimationFrame(() => {
      // Let the time for the page to fully load before scrolling to the step, otherwise the scroll position will be wrong
      if (isFirstRender.current) {
        isFirstRender.current = false;
        setTimeout(scrollToStep, 250);
        return;
      }

      scrollToStep();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      removeEventListener("scrollend", handleScrollEnd);
    };
  }, [currentStep]);

  const stepProps = {
    orderId,
    productId: product.id,
    product,
    orderConfiguration,
    registerStepSave: stepSaveCoordinator.registerStepSave,
    itemType,
    bulkGroup,
  };

  const goNextWhenSaved = async () => {
    if (isNavigatingAfterSave) {
      return;
    }

    setIsNavigatingAfterSave(true);
    try {
      let invalidSteps = stepSaveCoordinator.getInvalidSteps();
      if (invalidSteps.length > 0) {
        goTo(invalidSteps[0].stepKey);
        return;
      }

      await stepSaveCoordinator.validateAll();
      await stepSaveCoordinator.waitUntilIdle({
        isMutating: () => isMutationInFlightRef.current,
      });

      invalidSteps = stepSaveCoordinator.getInvalidSteps();
      if (invalidSteps.length > 0) {
        goTo(invalidSteps[0].stepKey);
        return;
      }

      await stepSaveCoordinator.flushAll();
      await stepSaveCoordinator.waitUntilIdle({
        isMutating: () => isMutationInFlightRef.current,
      });

      goNext();
    } catch {
      notifications.show({
        title: "Enregistrement en cours",
        message:
          "La sauvegarde prend plus de temps que prévu. Veuillez réessayer dans quelques instants.",
        color: "red",
      });
    } finally {
      setIsNavigatingAfterSave(false);
    }
  };

  return (
    <Stack w="100%">
      {bulkGroup.isBulkMode ? (
        <Title variant="display" size="lg">
          Vos{" "}
          <Text span inherit variant="accent">
            {translateProduct(product.type, { count: 2 }).toLowerCase()}
          </Text>
          .
        </Title>
      ) : (
        <Title variant="display" size="lg">
          Votre{" "}
          <Text span inherit variant="accent">
            {translateProduct(product.type, { count: 1 }).toLowerCase()}
          </Text>
          .
        </Title>
      )}
      <Divider />
      {activeSteps.map(step => (
        <Stack
          key={step.key}
          gap="s10"
          ref={step.key === currentStep ? currentStepComponentRef : null}
          data-product-step
        >
          <Box pos="relative">
            <Box
              inert={!step.reachable}
              opacity={step.reachable ? 1 : 0.5}
              aria-disabled={!step.reachable}
            >
              <step.Component
                {...stepProps}
                stepKey={step.key}
                bulk={{
                  isSupported: isBulkSupported,
                  onSubmit: applyTeethSelection,
                  preselectedTeeth,
                }}
                goToStep={() => goTo(step.key)}
              />
            </Box>
            {!step.reachable && (
              <Text
                pos="absolute"
                top="var(--mantine-spacing-md)"
                right="var(--mantine-spacing-md)"
                size="sm"
                c="dimmed"
              >
                Complétez l&apos;étape précédente
              </Text>
            )}
          </Box>
          <Divider />
        </Stack>
      ))}
      <StepNavigation
        onBack={goBack}
        onNext={goNextWhenSaved}
        isSubmitting={isNavigatingAfterSave || isMutationInFlight}
      />
    </Stack>
  );
};

const ProductConfiguration = () => {
  const { step: currentStep, productId = "" } = useParams<{
    step: string;
    productId: string;
  }>();
  const { orderId } = useOutletContext<{ orderId: string }>();
  const [activeStep, setActiveStep] = useState<StepKeys>();

  const orderConfiguration = useOrderConfiguration({ orderId: orderId ?? "" });

  const product = orderConfiguration.order?.products.find(
    orderProduct => orderProduct?.id === productId
  );

  const bulkGroup = useBulkProductGroup({
    leaderProduct: product,
    products: orderConfiguration.order?.products ?? [],
  });
  const { bulkProductFollowerIds } = bulkGroup;

  const steps = useMemo(
    () => (product ? getStepsForProduct(product) : []),
    [product]
  );
  const completedSteps = useMemo(
    () => (product ? getCompletedSteps(product) : []),
    [product]
  );
  const availableSteps = useMemo(
    () => steps.filter(step => product && !step.isSkipped(product)),
    [steps, product]
  );

  if (!orderId) {
    return <Navigate to="/orders" replace />;
  }

  // Loader is handled by the parent
  if (orderConfiguration.isLoading) {
    return null;
  }

  if (!orderConfiguration.order) {
    return <Navigate to="/orders" replace />;
  }

  if (!product) {
    return <Navigate to={buildAddProductPath(orderId)} replace />;
  }

  if (!currentStep || !availableSteps.some(step => step.key === currentStep)) {
    const firstUncompletedStep = availableSteps.find(
      step => !completedSteps.includes(step.key)
    );

    return (
      <Navigate
        to={buildProductStepPath(
          orderId,
          product.id,
          firstUncompletedStep?.key ?? availableSteps[0].key,
          bulkProductFollowerIds
        )}
        replace
      />
    );
  }

  return (
    <Group
      w="100%"
      h="100%"
      wrap="nowrap"
      align="stretch"
      py="s6"
      px="s20"
      gap="s12"
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <ProductConfigurationContent
          orderId={orderId}
          currentStep={currentStep}
          product={product}
          orderConfiguration={orderConfiguration}
          setActiveStep={setActiveStep}
          availableSteps={availableSteps}
        />
      </Box>
      <Box
        flex="0 0 auto"
        pos="sticky"
        top="200px"
        style={{
          alignSelf: "flex-start",
        }}
      >
        <ProductStepper
          key={productId}
          orderId={orderId}
          productId={product.id}
          product={product}
          steps={steps}
          completedStepKeys={completedSteps}
          bulkProductIds={bulkProductFollowerIds}
          activeStep={activeStep}
        />
      </Box>
    </Group>
  );
};

export default ProductConfiguration;
